import React from 'react'
import { useSelector } from 'react-redux'

import ErrorFallback from './error-fallback.component'
import { UseCloudFunction } from '@/utils/firebase.utils'

class ErrorBoundaryBase extends React.Component {

  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.reportError(error, errorInfo)
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  async reportError(error, errorInfo) {
    // only auto-report when we have a logged-in user profile, since
    // saveBugReport reads userProfile.appId directly and will throw otherwise
    const userProfile = this.props.userProfile
    if (!userProfile || !userProfile.appId) { return }

    try {
      await UseCloudFunction('saveBugReport', {
        bugReport: {
          title: `[Auto] ${error?.message || 'Unhandled error'}`.slice(0, 200),
          desc: [
            `URL: ${window.location.href}`,
            `Error: ${error?.stack || error}`,
            `Component stack:${errorInfo?.componentStack || 'n/a'}`
          ].join('\n\n'),
          source: 'error-boundary',
          status: 'active'
        },
        userProfile
      })
    } catch (reportErr) {
      console.error('Failed to auto-report caught error:', reportErr)
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          reload: this.handleReload,
          goHome: this.handleGoHome
        })
      }
      return (
        <ErrorFallback
          error={this.state.error}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome} />
      )
    }
    return this.props.children
  }
}

// wrapper so the class-based boundary can still read the current user
// profile for auto bug reporting without every caller needing to pass it in
const ErrorBoundary = ({ children, fallback, onError }) => {
  const userProfile = useSelector(state => state.userData.userProfile)
  return (
    <ErrorBoundaryBase userProfile={userProfile} fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundaryBase>
  )
}

export default ErrorBoundary
